import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { toast } from "sonner";
import { ShieldCheck, Camera, Mic, Monitor, ScanFace, Feather, Gauge, Lock, Sparkles, LockKeyhole } from "lucide-react";
import { SebQuitPassword } from "@/components/proctor/SebQuitPassword";

const TOGGLES = [
  ["require_camera", "Camera", "Require webcam monitoring", Camera],
  ["require_mic", "Microphone", "Require microphone access", Mic],
  ["require_screen", "Screen recording", "Record the exam screen", Monitor],
  ["identity_check", "ID + selfie check", "Candidate verifies identity before starting", ScanFace],
  ["lockdown", "Lockdown mode", "Full screen, no copy/paste; Chrome or Edge only", Lock],
  ["ai_second_look", "AI second look", "AI confirms or dismisses each camera flag", Sparkles],
  ["require_seb", "Requires Safe Exam Browser", "Test only opens in the Safe Exam Browser app (Windows, Mac, iPad)", LockKeyhole],
];
const ACTIONS = [["warn", "Warn and flag", "Candidate is warned; you review later"], ["pause", "Pause the test", "Waits until you let them continue"], ["end", "End and submit", "Test ends and answers are submitted"]];
const ACTION_SUMMARY = { warn: "warn only", pause: "pause after", end: "end after" };

const PRESET_KEYS = ["require_camera", "require_mic", "require_screen", "identity_check", "warn_threshold"];
const PRESETS = [
  ["Light", { require_camera: true, require_mic: false, require_screen: false, identity_check: false, warn_threshold: 5 }, Feather],
  ["Standard", { require_camera: true, require_mic: false, require_screen: false, identity_check: true, warn_threshold: 3 }, Gauge],
  ["Strict", { require_camera: true, require_mic: true, require_screen: true, identity_check: true, warn_threshold: 2 }, Lock],
];

const summary = (p) => {
  const on = TOGGLES.filter(([k]) => p[k]).map(([, label]) => (/^(AI|ID|Requires)/.test(label) ? label.replace("Requires ", "") : label.toLowerCase()));
  const act = p.violation_action || "warn";
  const rule = act === "warn" ? "warn on confirmed flags" : `${ACTION_SUMMARY[act]} ${p.warn_threshold} confirmed warning${p.warn_threshold === 1 ? "" : "s"}`;
  return `${on.length ? on.join(", ") : "No device checks"} · ${rule} · kept ${p.retention_days} days`;
};

function ViolationRule({ policy, save, saving }) {
  const act = policy.violation_action || "warn";
  return (
    <fieldset disabled={saving} className="space-y-2" data-testid="violation-action-control">
      <legend className="text-sm font-medium mb-2">On a confirmed violation</legend>
      <div className="grid grid-cols-3 gap-2">{ACTIONS.map(([k, label, desc]) => (
        <button key={k} onClick={() => save({ violation_action: k })} aria-pressed={act === k} data-testid={`violation-action-${k}`} title={desc}
          className={`rounded-lg border px-2 py-2 text-xs transition-colors ${act === k ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-muted"}`}>{label}</button>
      ))}</div>
      <p className="text-xs text-muted-foreground">{ACTIONS.find(([k]) => k === act)[2]}. Applies once the warning count below is reached.</p>
    </fieldset>
  );
}

function Presets({ policy, save }) {
  return (
    <div className="space-y-1.5" data-testid="policy-presets">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Quick presets</div>
      <div className="grid grid-cols-3 gap-2">
        {PRESETS.map(([label, vals, Icon]) => {
          const active = PRESET_KEYS.every((k) => policy[k] === vals[k]);
          return (
            <button key={label} onClick={() => save(vals)} data-testid={`preset-${label.toLowerCase()}`}
              className={`rounded-lg border p-2.5 text-center transition-colors ${active ? "border-primary bg-primary/10" : "border-border/60 hover:border-primary/40"}`}>
              <Icon className={`h-4 w-4 mx-auto mb-1 ${active ? "text-primary" : "text-muted-foreground"}`} />
              <div className="text-xs font-medium">{label}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Sensitivity({ policy, save, saving }) {
  const level = policy.sensitivity || "balanced";
  return (
    <fieldset disabled={saving} className="space-y-2" data-testid="sensitivity-control">
      <legend className="text-sm font-medium mb-2">Detection sensitivity</legend>
      <div className="grid grid-cols-3 gap-2">{["relaxed", "balanced", "strict"].map((l) => <button key={l} onClick={() => save({ sensitivity: l })} aria-pressed={level === l} data-testid={`sensitivity-${l}`} className={`rounded-lg border px-2 py-2 text-xs capitalize transition-colors ${level === l ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-muted"}`}>{l}</button>)}</div>
      <p className="text-xs text-muted-foreground" data-testid="sensitivity-description">{level === "strict" ? "Flags shorter speech-like bursts and lower-confidence phone/person sightings. Expect more false positives." : level === "relaxed" ? "Requires longer speech-like bursts and higher-confidence phone/person sightings. May miss brief events." : "Balances speech duration and phone/person confidence. Recommended starting point."} These are advisory signals, not proof of misconduct. Changes apply to newly opened sessions.</p>
    </fieldset>
  );
}

function PickNumber({ label, value, options, unit, onChange, testId }) {
  return (
    <div className="space-y-1.5">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <Select value={String(value)} onValueChange={(v) => onChange(Number(v))}>
        <SelectTrigger className="h-9" data-testid={testId}><SelectValue /></SelectTrigger>
        <SelectContent position="popper">{options.map((n) => <SelectItem key={n} value={String(n)}>{n} {unit(n)}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

function PolicyControls({ policy, save, saving }) {
  return (
    <div className="space-y-5 pt-2">
      <Presets policy={policy} save={save} />
      <div className="space-y-3">
        {TOGGLES.map(([key, label, desc, Icon]) => (
          <div key={key} className="flex items-center justify-between gap-3" data-testid={`policy-row-${key}`}>
            <div className="flex items-center gap-2 min-w-0">
              <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="min-w-0"><div className="text-sm">{label}</div><div className="text-[11px] text-muted-foreground truncate">{desc}</div></div>
            </div>
            <Switch checked={!!policy[key]} onCheckedChange={(v) => save({ [key]: v })} data-testid={`policy-${key}`} />
          </div>
        ))}
      </div>
      <Sensitivity policy={policy} save={save} saving={saving} />
      <ViolationRule policy={policy} save={save} saving={saving} />
      <div className="grid grid-cols-2 gap-3">
        <PickNumber label="Apply after" value={policy.warn_threshold} options={[1, 2, 3, 5, 8]} unit={(n) => `warning${n === 1 ? "" : "s"}`} onChange={(v) => save({ warn_threshold: v })} testId="policy-warn" />
        <PickNumber label="Keep snapshots" value={policy.retention_days} options={[30, 60, 90, 180, 365]} unit={() => "days"} onChange={(v) => save({ retention_days: v })} testId="policy-retention" />
      </div>
      <p className="text-[11px] text-muted-foreground flex items-start gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" /> All signals are advisory and reviewed by a person. Nothing here auto-rejects a candidate.{saving ? " Saving…" : ""}</p>
    </div>
  );
}

// One-line summary in the flow; the full settings open in a side panel.
export function ProctorPolicyCard({ jobId }) {
  const [policy, setPolicy] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get(`/jobs/${jobId}/proctoring-policy`).then((r) => setPolicy(r.data)).catch(() => {}); }, [jobId]);
  if (!policy) return null;

  const save = async (patch) => {
    const previous = policy;
    setPolicy({ ...policy, ...patch });
    setSaving(true);
    try { const { data } = await api.patch(`/jobs/${jobId}/proctoring-policy`, patch); setPolicy((p) => ({ ...data, seb_quit_password_set: p.seb_quit_password_set })); }
    catch { setPolicy(previous); toast.error("Could not save monitoring policy"); }
    finally { setSaving(false); }
  };

  return (
    <div className="hs-card flex items-center gap-3 px-5 py-4" data-testid="proctor-policy-card">
      <ShieldCheck className="h-5 w-5 text-primary shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold">Monitoring policy</div>
        <div className="text-xs text-muted-foreground truncate first-letter:uppercase" data-testid="proctor-policy-summary">{summary(policy)}</div>
      </div>
      <button onClick={() => setOpen(true)} data-testid="proctor-policy-toggle" className="text-sm text-primary font-medium hover:underline shrink-0">Change</button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="proctor-policy-sheet">
          <SheetHeader>
            <SheetTitle>Monitoring policy</SheetTitle>
            <SheetDescription>What candidates are asked to allow during the assessment. Changes save as you go.</SheetDescription>
          </SheetHeader>
          <PolicyControls policy={policy} save={save} saving={saving} />
          {policy.require_seb && <SebQuitPassword jobId={jobId} isSet={!!policy.seb_quit_password_set} onChange={(v) => setPolicy((p) => ({ ...p, seb_quit_password_set: v }))} />}
        </SheetContent>
      </Sheet>
    </div>
  );
}
